import React, { useState, useEffect } from "react";
import { Form, Modal, Button, Spinner } from "react-bootstrap";
import PlaceholderImg from '../../../assets/img/PlaceholderImg.png'
import axios from "axios";
import { API_BASE_URL } from "config.js";
import Swal from "sweetalert2";
import { compressImageIfNeeded } from "utils/mmsImage";

const readFileAsDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsDataURL(blob);
  });

const MessageStep = ({ data, setData, nextStep, prevStep, setCredits, userForceTest, isProcessing, setIsProcessing }) => {
  const [showTestModal, setShowTestModal] = useState(false);
  const [showRotationWarningModal, setShowRotationWarningModal] = useState(false);
  const [testNumber, setTestNumber] = useState("");
  const [file, setFile] = useState(null);
  const [showMMSUpload, setShowMMSUpload] = useState(false);
  const [testSentSuccess, setTestSentSuccess] = useState(data.testSent || false);
  const [sendingTest, setSendingTest] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [testSendError, setTestSendError] = useState(null);
  const [mediaPreviewIsVideo, setMediaPreviewIsVideo] = useState(false);

  const isVideoMedia =
    mediaPreviewIsVideo ||
    data.mediaMimeType === "video/mp4" ||
    data.mediaMimeType === "video/quicktime" ||
    /\.(mp4|mov|m4v)(\?|$)/i.test(data.mediaUrl || "") ||
    Boolean(file && (file.type === "video/mp4" || file.type === "video/quicktime" || /\.(mp4|mov|m4v)$/i.test(file.name || "")));

  const activePoolCount = Array.isArray(data.phoneNumbers) ? data.phoneNumbers.length : 0;
  console.log("MessageStep render. data.phoneNumbers:", data.phoneNumbers, "activePoolCount:", activePoolCount);

  useEffect(() => {
    if (!data.messageType) {
      setData({ ...data, messageType: data.mediaUrl ? "mms" : "sms" });
    }
    if (data.messageType === "mms" || data.mediaUrl) {
      setShowMMSUpload(true);
    }

    const fetchCustomFields = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios.get(`${API_BASE_URL}/custom-fields`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          setCustomFields(res.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching custom fields:", err);
      }
    };

    fetchCustomFields();
  }, []);

  const handleChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  // This will add the dynamic variable to the message template
  const addVariableToTemplate = (variable) => {
    const newMessageTemplate = data.messageTemplate
      ? `${data.messageTemplate} ${variable}` // Append the variable to the message
      : variable; // If the message template is empty, just set the variable
    setData({ ...data, messageTemplate: newMessageTemplate });
  };

  const processAndUploadFile = async (selectedFile) => {
    if (!selectedFile) return;

    const ext = selectedFile.name.includes(".")
      ? selectedFile.name.substring(selectedFile.name.lastIndexOf(".")).toLowerCase()
      : "";
    const validExts = [".jpg", ".jpeg", ".png", ".gif", ".mp4", ".mov", ".m4v"];
    const validMimes = ["image/jpeg", "image/png", "image/gif", "video/mp4", "video/quicktime", "video/x-m4v"];
    const isValid = validMimes.includes(selectedFile.type) || validExts.includes(ext);

    if (!isValid) {
      return Swal.fire(
        "Unsupported Format",
        "Twilio MMS only supports JPEG, PNG, GIF images and MP4/MOV videos. Please select a supported file (.jpg, .jpeg, .png, .gif, .mp4, .mov).",
        "warning"
      );
    }

    const isVideo = selectedFile.type === "video/mp4" || selectedFile.type === "video/quicktime" || selectedFile.type === "video/x-m4v" || [".mp4", ".mov", ".m4v"].includes(ext);

    if (isVideo && selectedFile.size > 5 * 1024 * 1024) {
      return Swal.fire(
        "Video Too Large",
        "MP4 videos must be 5 MB or smaller to meet Twilio MMS limits.",
        "warning"
      );
    }

    if (!isVideo && selectedFile.size > 15 * 1024 * 1024) {
      return Swal.fire("File Too Large", "Maximum supported image upload is 15MB.", "warning");
    }

    if ((ext === ".gif" || selectedFile.type === "image/gif") && selectedFile.size > 3.5 * 1024 * 1024) {
      return Swal.fire(
        "GIF Too Large",
        "Animated GIFs must be 3.5MB or smaller to meet MMS network limits.",
        "warning"
      );
    }

    setUploadingMedia(true);
    setFile(selectedFile);
    setMediaPreviewIsVideo(isVideo);

    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let uploadedMedia = null;

      if (isVideo) {
        // Use direct-to-Vercel-Blob upload for larger MP4s (> 3 MB) so Base64 overhead
        // does not hit Vercel's 4.5 MB serverless request body limit.
        let useBase64Fallback = selectedFile.size <= 3 * 1024 * 1024;
        if (!useBase64Fallback) {
          const tokenRes = await axios.post(
            `${API_BASE_URL}/media/upload-token`,
            {
              fileName: selectedFile.name,
              mimeType: "video/mp4",
              size: selectedFile.size,
            },
            { headers }
          );

          if (tokenRes.data.success && tokenRes.data.clientToken) {
            const putRes = await fetch(`https://blob.vercel-storage.com/${tokenRes.data.pathname}`, {
              method: "PUT",
              headers: {
                authorization: `Bearer ${tokenRes.data.clientToken}`,
                "x-api-version": "7",
                "x-content-type": "video/mp4",
              },
              body: selectedFile,
            });
            if (!putRes.ok) {
              throw new Error("Direct video upload to cloud storage failed.");
            }
            const blobData = await putRes.json();
            const completeRes = await axios.post(
              `${API_BASE_URL}/media/upload-complete`,
              {
                url: blobData.url,
                fileName: selectedFile.name,
                mimeType: "video/mp4",
                size: selectedFile.size,
              },
              { headers }
            );
            if (completeRes.data.success) {
              uploadedMedia = completeRes.data.data;
            }
          } else if (tokenRes.data.localFallback) {
            useBase64Fallback = true;
          }
        }

        if (useBase64Fallback && !uploadedMedia) {
          const dataUrl = await readFileAsDataUrl(selectedFile);
          const res = await axios.post(
            `${API_BASE_URL}/media/upload`,
            {
              fileName: selectedFile.name,
              fileData: dataUrl,
            },
            { headers }
          );
          if (res.data.success) {
            uploadedMedia = res.data.data;
          }
        }
      } else {
        const { dataUrl, fileName } = await compressImageIfNeeded(selectedFile);

        // Verify encoded base64 fits within Vercel's 4.5 MB serverless limit (~4.3 MB string threshold)
        if (dataUrl.length > 4.3 * 1024 * 1024) {
          setUploadingMedia(false);
          setFile(null);
          return Swal.fire(
            "File Too Large",
            "The image could not be compressed under network limits. Please select a smaller image.",
            "warning"
          );
        }

        const res = await axios.post(
          `${API_BASE_URL}/media/upload`,
          {
            fileName,
            fileData: dataUrl,
          },
          { headers }
        );
        if (res.data.success) {
          uploadedMedia = res.data.data;
        }
      }

      if (uploadedMedia) {
        // Store the direct blob URL, mediaId for proxy routing, and mimeType for video/image preview
        setData((prev) => ({
          ...prev,
          mediaUrl: uploadedMedia.url,
          mediaId: uploadedMedia._id,
          mediaMimeType: uploadedMedia.mimeType,
        }));
        Swal.fire(
          isVideo ? "Video Uploaded!" : "Image Uploaded!",
          "Your media has been uploaded and attached to the MMS message template.",
          "success"
        );
      }
    } catch (err) {
      console.error("Media upload failed:", err);
      Swal.fire("Upload Failed", err.response?.data?.message || err.message || "Failed to upload media asset.", "error");
      setData((prev) => ({ ...prev, mediaUrl: null, mediaId: null, mediaMimeType: null }));
      setFile(null);
      setMediaPreviewIsVideo(false);
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      processAndUploadFile(selectedFile);
    }
    // Reset file input value so re-uploading the same file triggers onChange
    e.target.value = "";
  };

  const handleMessageTypeToggle = (type) => {
    setData({ ...data, messageType: type });
    setShowMMSUpload(type === "mms");
  };

  const complianceOption = data.complianceOption || "Reply STOP to unsubscribe";
  const complianceLength = (complianceOption === "none" || complianceOption === "None") ? 0 : complianceOption.length + 2;
  const baseLength = data.messageTemplate?.length || 0;
  const totalLength = baseLength > 0 ? baseLength + complianceLength : 0;
  const segmentSize = data.messageType === "mms" ? 1600 : 160;
  const segments = Math.ceil(totalLength / segmentSize) || 0;
  
  let costPerContact = 0;
  if (data.messageType === "mms") {
    costPerContact = segments * 8;
  } else {
    costPerContact = segments * 3;
  }

  /**
   * Intercept "Continue" to show a rotation warning if:
   * - The pool has ≥2 numbers (rotation is possible)
   * - The user hasn't explicitly set a value OR opted out (messagesPerNumber is still "" — untouched)
   * If pool < 2 or user already made a choice (number set or explicitly null), go straight to nextStep.
   */
  // Draft Saving logic
  const saveDraftAndGo = async (updatedData = data, navigateNext = true) => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      const draftPayload = {
        name: updatedData.name,
        contactListId: updatedData.contactListId,
        messageBody: updatedData.messageTemplate,
        campaignType: updatedData.campaignType || "broadcast",
        complianceOption: updatedData.complianceOption || "Reply STOP to unsubscribe",
        isDraft: true,
        draftId: updatedData.draftId || null,
        phoneNumbers: updatedData.phoneNumbers || [],
        messagesPerNumber: (updatedData.messagesPerNumber !== "" && updatedData.messagesPerNumber !== undefined) ? parseInt(updatedData.messagesPerNumber, 10) : undefined,
        mediaUrl: updatedData.mediaId
          ? `${API_BASE_URL}/media/proxy/${updatedData.mediaId}`
          : (updatedData.mediaUrl || null),
      };

      const res = await axios.post(`${API_BASE_URL}/campaigns`, draftPayload, {
        headers: authHeaders
      });

      setIsProcessing(false);
      if (res.data.success && res.data.campaign) {
        setData({
          ...updatedData,
          draftId: res.data.campaign._id,
          approvalStatus: res.data.campaign.approvalStatus || "none"
        });
        if (navigateNext) {
          nextStep();
        } else {
          prevStep();
        }
      } else {
        Swal.fire("Error", "Failed to save campaign draft. Please try again.", "error");
      }
    } catch (err) {
      setIsProcessing(false);
      console.error("Error saving campaign draft:", err);
      Swal.fire("Error", err.response?.data?.message || "Failed to save campaign draft.", "error");
    }
  };

  const handleContinue = () => {
    saveDraftAndGo(data);
  };

  return (
    <div>
      <h5 style={{ fontWeight: 700, marginBottom: "25px" }}>Message Composition</h5>

      <Form>
        {/* Message Type Checkboxes */}
        <Form.Group className="mb-4">
          <div className="d-flex gap-5">
            {["sms", "mms"].map((type) => (
              <Form.Check
                key={type}
                type="checkbox"
                className="cutomCheckbox"
                label={type.toUpperCase()}
                id={`type-${type}`}
                checked={data.messageType === type}
                onChange={() => handleMessageTypeToggle(type)}
                style={{
                  fontWeight: "600",
                  color: "#5E5E5E",
                  cursor: "pointer",
                  userSelect: "none",
                  marginRight: "50px"
                }}
              />
            ))}
          </div>
        </Form.Group>

        <div
          style={{
            maxHeight: showMMSUpload ? "440px" : "0",
            overflow: "hidden",
            transition: "max-height 0.5s ease-in-out",
            marginBottom: "0px",
          }}
        >
          <div
            className="d-flex flex-column align-items-center justify-content-center text-center"
            style={{
              minHeight: "200px",
              border: isDragging ? "4px solid #004992" : "4px dashed #c7c7c7",
              backgroundColor: isDragging ? "#f0f7ff" : "#ffffff",
              borderRadius: "10px",
              transition: "all 0.2s ease-in-out",
              cursor: "pointer",
              padding: "12px"
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isDragging) setIsDragging(true);
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
              if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                processAndUploadFile(e.dataTransfer.files[0]);
              }
            }}
            onClick={(e) => {
              // Avoid triggering if click was on the upload button or video player itself
              if (
                e.target.tagName !== "BUTTON" &&
                e.target.tagName !== "VIDEO" &&
                !e.target.closest("button")
              ) {
                document.getElementById("fileInput").click();
              }
            }}
          >
            <i className={isVideoMedia ? "fas fa-film" : "fas fa-image"} style={{ fontSize: "48px", color: isDragging ? "#004992" : "#6c757d" }}></i>
            <p style={{ fontWeight: 700 }} className="m-0 text-muted">Drop your file here or click upload</p>
            <p style={{ fontWeight: 700 }} className="text-muted small m-0">
              Supports JPEG, PNG, GIF images (up to 15MB, auto-optimized) and MP4 video (up to 5MB; under 3.5MB recommended for best carrier delivery)
            </p>

            {file ? (
              <div className="text-success fw-semibold mt-2 d-flex flex-column align-items-center">
                <span>File selected: {file.name}</span>
                {data.mediaUrl && isVideoMedia && (
                  <video
                    src={data.mediaUrl}
                    controls
                    muted
                    playsInline
                    onClick={(e) => e.stopPropagation()}
                    style={{ maxWidth: "180px", maxHeight: "120px", marginTop: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
                  />
                )}
              </div>
            ) : data.mediaUrl ? (
              <div className="text-success fw-semibold mt-2 d-flex flex-column align-items-center">
                <span>{isVideoMedia ? "Attached Video:" : "Attached Image:"}</span>
                {isVideoMedia ? (
                  <video
                    src={data.mediaUrl}
                    controls
                    muted
                    playsInline
                    onClick={(e) => e.stopPropagation()}
                    style={{ maxWidth: "180px", maxHeight: "120px", marginTop: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
                  />
                ) : (
                  <img
                    src={data.mediaUrl}
                    alt="Attached Media"
                    onError={() => setMediaPreviewIsVideo(true)}
                    style={{ maxWidth: "120px", maxHeight: "120px", marginTop: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
                  />
                )}
              </div>
            ) : null}

            <Button style={{ color: 'black', background: "#e0e0e0", fontWeight: "700", borderRadius: '8px' }}
              className="mt-3 px-4 py-2"
              disabled={uploadingMedia}
              onClick={(e) => {
                e.stopPropagation();
                document.getElementById("fileInput").click();
              }}
            >
              {uploadingMedia ? <Spinner animation="border" size="sm" /> : "Upload"}
            </Button>

            <input
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.mp4,.mov,image/jpeg,image/png,image/gif,video/mp4,video/quicktime"
              id="fileInput"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>
        </div>

        {/* Textarea with counter */}
        <Form.Group className="mb-4">
          <Form.Label style={{ fontWeight: "600", color: "#5E5E5E", marginTop: "20px" }}>Message Template</Form.Label>
          <div className="position-relative">
            <Form.Control
              as="textarea"
              name="messageTemplate"
              value={data.messageTemplate || ""}
              onChange={handleChange}
              placeholder="Type your message..."
              rows={4}
              style={{
                border: "2px solid #8E8E8E", borderRadius: "10px", fontSize: "14px",
                padding: "10px",
              }}
            />
          </div>
          <div className="mt-2 d-flex justify-content-between align-items-start" style={{ fontSize: "14px", color: "#5E5E5E" }}>
            <div>
              <div style={{ fontWeight: "600" }}>
                {segments} segment{segments !== 1 ? "s" : ""} ({totalLength}/{segments > 0 ? segments * segmentSize : segmentSize} chars)
              </div>
              <div style={{ fontSize: "12px", color: "#8E8E8E", marginTop: "4px" }}>
                *Includes compliance suffix character length. 
                <br/>
                *Note: Placeholders like {"{{first_name}}"} are estimated literally. Actual cost may vary slightly based on final replacement length.
              </div>
            </div>
            <div style={{ fontWeight: "700", color: "#004992", textAlign: "right" }}>
              Estimated cost: {costPerContact}¢ per contact
            </div>
          </div>
        </Form.Group>

        {/* Available Fields — right below the message textarea */}
        <div
          className="mb-4 p-3"
          style={{ border: "2px solid #8E8E8E", borderRadius: "10px" }}
        >
          <strong style={{ fontWeight: "600", color: "#5E5E5E" }}>Available Fields</strong>
          <div className="d-flex flex-wrap gap-2 mt-2">
            {["{{first_name}}", "{{last_name}}", ...customFields.map(f => `{{${f.name}}}`)].map((variable) => (
              <button
                key={variable}
                className="py-2 px-3"
                style={{
                  border: "2px solid #8E8E8E", borderRadius: "10px", fontWeight: 600,
                  fontSize: "13px", background: 'white', cursor: 'pointer'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  addVariableToTemplate(variable);
                }}
              >
                {variable}
              </button>
            ))}
          </div>
        </div>

        <Form.Group className="mb-4">
          <Form.Label style={{ fontWeight: "600", color: "#5E5E5E" }}>Opt-Out Compliance Message</Form.Label>
          <Form.Control
            as="select"
            name="complianceOption"
            value={data.complianceOption || "Reply STOP to unsubscribe"}
            onChange={(e) => {
              setData({ ...data, complianceOption: e.target.value });
            }}
            style={{
              border: "2px solid #8E8E8E",
              borderRadius: "10px",
              fontSize: "14px",
              padding: "10px",
              height: "45px"
            }}
          >
            <option value="Reply STOP to unsubscribe">Reply STOP to unsubscribe (Standard)</option>
            <option value="Reply STOPALL to unsubscribe">Reply STOPALL to unsubscribe</option>
            <option value="Reply UNSUBSCRIBE to opt out">Reply UNSUBSCRIBE to opt out</option>
            <option value="Reply CANCEL to unsubscribe">Reply CANCEL to unsubscribe</option>
            <option value="Reply END to unsubscribe">Reply END to unsubscribe</option>
            <option value="Reply QUIT to opt out">Reply QUIT to opt out</option>
          </Form.Control>
        </Form.Group>



        {/* Navigation Buttons */}
        <div className="d-flex mt-4 justify-content-between">
          <button
            type="button"
            className="btn"
            disabled={isProcessing}
            style={{
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              borderRadius: "6px",
              padding: "8px 25px",
              fontWeight: "600",
              color: 'black'
            }}
            onClick={() => saveDraftAndGo(data, false)}
          >
            Back
          </button>

          <div className="d-flex gap-3 align-items-center">
            {!testSentSuccess && (
              <span className="text-muted mr-2" style={{ fontSize: "0.85rem", fontWeight: "600" }}>
                *Send a test SMS to unlock Continue
              </span>
            )}
            <button
              type="button"
              className="btn btn-warning fw-bold text-dark px-4 py-2"
              disabled={isProcessing}
              style={{
                borderRadius: "6px",
                padding: "8px 25px",
              }}
              onClick={() => setShowTestModal(true)}
            >
              {testSentSuccess
                ? `Send Test ${data.messageType === "mms" ? "MMS" : "SMS"} Again`
                : `Send Test ${data.messageType === "mms" ? "MMS" : "SMS"}`}
            </button>
            {testSentSuccess && (
              <button
                id="message-step-continue"
                type="button"
                className="btn"
                disabled={isProcessing}
                style={{
                  backgroundColor: isProcessing ? "#709bc6" : "#004992",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 25px",
                  fontWeight: "600",
                }}
                onClick={handleContinue}
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </Form >



      {/* ── Test Message Modal ─────────────────────────────────────────────── */}
      {showTestModal && (
        <Modal
          show={showTestModal}
          onHide={() => setShowTestModal(false)}
          centered
          size="md"
          contentClassName="p-3"
          style={{ borderRadius: "12px" }}
        >
          <Modal.Header style={{ padding: '0' }} closeButton>
          </Modal.Header>
          <Modal.Title style={{ fontSize: "2.2rem", fontWeight: "700", margin: "0 0 10px 0", paddingLeft: '24px' }}>
            Test Campaign
          </Modal.Title>

          <Modal.Body style={{ paddingTop: '0' }}>
            <p style={{ color: "#6C6C6C", fontSize: "16px", marginBottom: "1.5rem" }}>
              Test your {data.messageType?.toUpperCase() || "SMS"} Campaign before finalizing
            </p>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: 600 }}>Send test to a phone number</Form.Label>
              <Form.Control
                type="text"
                placeholder="xxx-xxx-xxxx"
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
                style={{
                  border: "2px solid #c7c7c7", height: "42px",
                  fontSize: "14px",
                }}
              />
              <p className="text-muted mb-2" style={{ fontSize: "0.8rem", marginTop: "1rem" }}>
                ⚠️ <strong>Billing Notice:</strong> Sending a test message will deduct a standard charge of <strong>{costPerContact}¢</strong> (default cost and cost scaling) from your balance.
              </p>
            </Form.Group>

            <div
              className="p-3 rounded"
              style={{
                border: "2px solid #c7c7c7",
                borderRadius: "10px",
                background: "#fff",
              }}
            >

              <div
                className="text-white p-3"
                style={{
                  backgroundColor: "#007BFF",
                  borderRadius: "10px",
                  fontSize: "14px",
                  fontWeight: 500,
                  width: "fit-content",
                  maxWidth: "55%",
                  boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)",
                  alignSelf: "flex-end",
                  marginLeft: "auto",
                  lineHeight: '1rem'
                }}
              >
                {data.messageType === "mms" && (
                  <div className="d-flex justify-content-start mb-2">
                    {data.mediaUrl ? (
                      isVideoMedia ? (
                        <video
                          src={data.mediaUrl}
                          controls
                          muted
                          playsInline
                          style={{ width: "100%", maxHeight: "140px", borderRadius: "6px", backgroundColor: "#000" }}
                        />
                      ) : (
                        <img
                          src={data.mediaUrl}
                          alt="MMS Preview"
                          onError={() => setMediaPreviewIsVideo(true)}
                          style={{ width: "100%", maxHeight: "140px", objectFit: "contain", borderRadius: "6px" }}
                        />
                      )
                    ) : (
                      <div
                        style={{
                          backgroundImage: `url(${PlaceholderImg})`,
                          backgroundRepeat: "no-repeat",
                          backgroundSize: "contain", backgroundPosition: "center",
                          width: "100%",
                          height: "100px",
                          borderRadius: "6px",
                        }}
                      ></div>
                    )}
                  </div>
                )}

                {data.messageTemplate || "Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Donec quam felis, ultricies nec, pellentesque eu, pretium quis, sem. Nulla consequat massa quis enim"}
              </div>
            </div>
          </Modal.Body>

          {testSendError && (
            <div
              style={{
                margin: "0 24px 16px",
                padding: "12px 16px",
                background: "#fff3f3",
                border: "1px solid #f5c6cb",
                borderRadius: "8px",
                color: "#842029",
                fontSize: "13px",
                fontWeight: 500,
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <span style={{ fontSize: "16px", lineHeight: "1.2" }}>⚠️</span>
              <div>
                <strong style={{ display: "block", marginBottom: "2px" }}>Send Failed</strong>
                {testSendError}
              </div>
              <button
                onClick={() => setTestSendError(null)}
                style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#842029", fontSize: "16px", lineHeight: 1 }}
              >×</button>
            </div>
          )}

          {/* Success banner — shown instead of closing modal */}
          {testSentSuccess && (
            <div
              style={{
                margin: "0 24px 16px",
                padding: "12px 16px",
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: "8px",
                color: "#166534",
                fontSize: "13px",
                fontWeight: 500,
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <span style={{ fontSize: "16px", lineHeight: "1.2" }}>✅</span>
              <div>
                <strong style={{ display: "block", marginBottom: "2px" }}>Test {data.messageType === "mms" ? "MMS" : "SMS"} Sent!</strong>
                Your message is on its way and should arrive shortly. If you’re happy with it, click <strong>Approve &amp; Continue</strong>. Otherwise click <strong>Edit Message</strong> to make changes.
              </div>
            </div>
          )}

          <Modal.Footer className="d-flex justify-content-between">
            <Button
              variant="light"
              style={{
                backgroundColor: "transparent",
                border: "none",
                fontWeight: "600",
                color: "#000",
              }}
              onClick={() => {
                setShowTestModal(false);
                setTestSentSuccess(false); // allow re-testing after editing
              }}
            >
              {testSentSuccess ? "Edit Message" : "Back"}
            </Button>

            {testSentSuccess ? (
              // After a successful test: Approve & Continue advances to next step
              <Button
                variant="primary"
                style={{
                  backgroundColor: "#16a34a",
                  border: "none",
                  fontWeight: "600",
                  borderRadius: "8px",
                  padding: "8px 25px",
                }}
                onClick={() => {
                  setShowTestModal(false);
                  handleContinue();
                }}
              >
                Approve &amp; Continue
              </Button>
            ) : (
              // Before a successful test: Send button
              <Button
                variant="primary"
                style={{
                  backgroundColor: "#004992",
                  border: "none",
                  fontWeight: "600",
                  borderRadius: "8px",
                  padding: "8px 25px",
                }}
                disabled={sendingTest}
              onClick={async () => {
                if (!testNumber) {
                  return Swal.fire("Input Required", "Please enter a valid phone number to receive the test message.", "warning");
                }
                setSendingTest(true);
                setTestSendError(null);
                try {
                  const token = localStorage.getItem("token");
                  const headers = token ? { Authorization: `Bearer ${token}` } : {};
                  const res = await axios.post(`${API_BASE_URL}/campaigns/send-test`, {
                    toPhoneNumber: testNumber,
                    messageBody: data.messageTemplate,
                    // Use proxy URL when mediaId is available so Twilio fetches from our domain
                    // (direct Vercel Blob CDN URLs can cause Twilio Error 12300)
                    mediaUrl: data.mediaId
                      ? `${API_BASE_URL}/media/proxy/${data.mediaId}`
                      : data.mediaUrl,
                    complianceOption: data.complianceOption || "Reply STOP to unsubscribe",
                    messageType: data.messageType,
                    campaignId: data.draftId || null
                  }, { headers });

                  if (res.data.success) {
                    // Keep modal open — show the green success banner and Approve & Continue button
                    setTestSentSuccess(true);
                    setData(prev => ({
                      ...prev,
                      approvalStatus: userForceTest ? "pending" : "none",
                      testSent: true
                    }));
                    // Deduct test cost from local credits so the Review step shows the updated balance
                    if (setCredits) {
                      setCredits(prev => Math.max(0, prev - costPerContact));
                    }
                  }
                } catch (error) {
                  console.error("Test send failed:", error);
                  const msg = error.response?.data?.message || "Failed to dispatch test message.";
                  setTestSendError(msg);
                } finally {
                  setSendingTest(false);
                }
              }}
            >
              {sendingTest ? <Spinner animation="border" size="sm" /> : "Send"}
            </Button>
            )}

          </Modal.Footer>
        </Modal>
      )}

    </div>
  );
};

export default MessageStep;
